import React, {FC, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import TnToggleSwitch from '@touchnet/react-toggle-switch';
import {TableFilter, TablePagination, TnReactTable, useDatatable} from '@touchnet/react-table';
import {
    CellContext,
    createColumnHelper,
    getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel,
} from '@tanstack/react-table';

import {Term} from '~/types/term-type';
import {actions} from "~/state/reducers/term-reducer.ts";

interface ComponentProps {
    visibleTerms: Term[];
    allTerms: {active: Term[], inactive: Term[]};
}

export const TermsImportedTable: FC<ComponentProps> = ({visibleTerms, allTerms}: ComponentProps) => {
    const dispatch = useDispatch();

    // TODO: remove this local date function and pull in formatDate 
    // from react-intl with useIntl() in the same way that Advisor does.
    const formatDate = (ts: number) => {
        if (!ts) {
            return '';
        }

        const date = new Date(Number(ts));
        return date.toLocaleDateString('en-US', { timeZone: 'UTC' });
    };

    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<Term>();

        const toggleStatus = (checked: boolean, e: MouseEvent, id: string) => {
            const allTermsArray: Term[] = allTerms.active.concat(allTerms.inactive)
            const termToToggle: Term = allTermsArray.find(term => term.id === id);
            if (termToToggle !== undefined) {
                dispatch(actions.updateTerms([{
                    ...termToToggle,
                    enabled: checked
                }]));
            }
        };

        const formatActivate = (enabled: boolean, termCell: CellContext<Term, boolean>) => { 
            return (
            <TnToggleSwitch
                id={termCell.row.original.id}
                checked={enabled === true}
                onChange={toggleStatus}
            />
        )};

        return [
            columnHelper.accessor('code', {
                header: 'Code',
                sortingFn: 'basic',
            }),
            columnHelper.accessor('description', {
                header: 'Description',
                sortingFn: 'basic',
            }),
            columnHelper.accessor(row => formatDate(row.startDateMillis), {
                id: 'startDateMillis',
                header: 'Start Date',
                sortingFn: 'numberBoolean',
            }),
            columnHelper.accessor(row => formatDate(row.endDateMillis), {
                id: 'endDateMillis',
                header: 'End Date',
                sortingFn: 'numberBoolean',
            }),
            columnHelper.accessor('enabled', {
                header: 'Status',
                cell: termEnabled => formatActivate(termEnabled.getValue(), termEnabled),
                sortingFn: 'basic',
            }),
        ];
    }, [allTerms]);

    const table = useDatatable({
        columns: columns,
        data: visibleTerms,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });
        
    return (
        <>
            <hr />
            <h3 className="mt-4 h5 mb-3">Imported Terms</h3>
            <div className="table-search-right">
                <TableFilter table={table} />
            </div>
            <TnReactTable table={table} noDataText={'No Terms Found'} />
            <TablePagination table={table} />
        </>
    );
}

export default TermsImportedTable;
